import { useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  Typography,
} from '@mui/material'
import { fetchSession } from '~/adapter/api/deviceAuthSession'
import logoSrc from '~/assets/logo/logo.svg'
import { selectIsAuthed, useSessionStore } from '~/stores/session/session'
import { sleepSeconds } from '~/utils/promise'
import type { SxProps, Theme } from '@mui/material'

// 再試行が一瞬で完了すると「ボタンが反応しない」と誤解されるため、
// ローディング表示に最低限の時間を保証する
const RETRY_MIN_LOADING_SECONDS = 0.5

const styles = {
  // Dialog デフォルトの zIndex（modal=1300）のままだと、裏の画面が出す
  // Snackbar（1400）がこのエラー画面の上に重なって矛盾した誘導になるため、
  // snackbar より手前にする。
  dialog: theme => ({
    zIndex: theme.zIndex.snackbar + 1,
  }),
  paper: theme => ({
    backgroundColor: theme.customPalette.bg,
    display: 'flex',
    overflowY: 'auto',
    padding: 4,
  }),
  content: {
    width: '100%',
    maxWidth: '600px',
    display: 'flex',
    flexDirection: 'column',
    // flex の中央寄せ（justify/align center）だとビューポートより縦に長いとき
    // 上端がスクロール到達不能になるため、margin: auto で安全に中央寄せする
    margin: 'auto',
  },
} as const satisfies Record<string, SxProps<Theme>>

/**
 * キオスク端末アカウントのトークン取得が IP アドレス制限で拒否されたときの
 * エラー画面（全画面オーバーレイ）。GlobalLayout が useIpRestrictionStore の
 * フラグを購読して表示する。制限中に背後の画面を操作できないよう、
 * フォーカストラップ・aria-modal・背景の inert 化を持つ MUI Dialog で全面を覆う
 *（キーボードやスクリーンリーダー経由の到達も防ぐ。閉じる手段は再試行の成功のみ）。
 *
 * 再試行成功時は fetchSession 内でフラグが解除されるためオーバーレイは自動で閉じる。
 * 認証済み（操作中）の場合はリロードすると編集中データ（メモリのみ）が消えるため
 * リロードせず、未認証（ログイン前）の場合のみ認証フローからやり直す。
 */
export function IpRestrictionError() {
  const [isRetrying, setIsRetrying] = useState(false)
  const [isRetryFailed, setIsRetryFailed] = useState(false)
  const isAuthed = useSessionStore(selectIsAuthed)

  const handleRetry = async () => {
    setIsRetrying(true)
    setIsRetryFailed(false)
    // 直列で待つと常に「待機 + fetch」の合計時間になるため、並行にして
    // max(最低保証, fetch 時間) にする
    const [, result] = await Promise.all([
      sleepSeconds(RETRY_MIN_LOADING_SECONDS),
      fetchSession(),
    ])
    setIsRetrying(false)
    if (result.success) {
      if (!isAuthed) {
        window.location.replace('/')
      }
      return
    }
    setIsRetryFailed(true)
  }

  return (
    <Dialog
      open
      fullScreen
      disableEscapeKeyDown
      sx={styles.dialog}
      slotProps={{ paper: { sx: styles.paper } }}
    >
      <Box sx={styles.content}>
        <Box
          component="img"
          src={logoSrc}
          alt="カミナシ レポート"
          sx={{ width: '250px', alignSelf: 'center' }}
        />
        <Typography variant="h1" sx={{ mt: 6, textAlign: 'center' }}>
          ネットワークが許可されていないため、アクセスできません
        </Typography>
        <Typography variant="body2" sx={{ mt: 4 }}>
          セキュリティ保護のため、この端末はオフィスのWi-Fiなど、許可されたネットワークからのみご利用いただけます。
        </Typography>
        <Typography variant="body2" sx={{ mt: 4 }}>
          ネットワークへの接続をご確認の上、もう一度お試しください。
        </Typography>
        <Typography variant="body2" sx={{ mt: 4 }}>
          解決しない場合は、社内のシステム管理者までご連絡ください。
        </Typography>
        {isRetryFailed && (
          <Alert severity="error" sx={{ mt: 4 }}>
            <AlertTitle sx={{ fontWeight: 'bold' }}>
              再試行に失敗しました
            </AlertTitle>
            {/* IP 制限以外の失敗（オフライン・サーバー障害等）でも表示されるが、
                レアかつ再試行で自己回復するため文言は分岐しない（PR レビューで議論済み） */}
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              このネットワークからはご利用いただけません。社内のシステム管理者へご連絡ください。
            </Typography>
          </Alert>
        )}
        <Button
          onClick={handleRetry}
          loading={isRetrying}
          variant="contained"
          sx={{ mt: 4, alignSelf: 'center' }}
          size="large"
        >
          再試行
        </Button>
      </Box>
    </Dialog>
  )
}
